import { inject, Injectable, signal } from '@angular/core';
import { KycService } from '../../services/kyc-service';
import { finalize } from 'rxjs';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import {
  BusinessInformationResponse,
  DocumentResponseDTO,
} from '../../../core/models/kyc.model';
import { Dialog, FinancialInstitution, Notification } from '@shared';
import { Location } from '@angular/common';
import { AuthService } from '@auth';

export interface MerchantKYCBasicInfoRequest {
  businessType: string;
  businessDescription: string;
}

export interface MerchantKYCBasicInfoForm {
  businessType: FormControl<string>;
  businessDescription: FormControl<string>;
}

@Injectable({
  providedIn: 'root',
})
export class MerchantKycStore {
  private service = inject(KycService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private notification = inject(Notification);
  public isLoading = signal<boolean>(false);
  public gettingDocuments = signal<boolean>(false);
  public gettingBankList = signal<boolean>(false);
  public gettingOwners = signal<boolean>(false);
  public gettingBusinessInfo = signal<boolean>(false);
  private businessInfo = signal<BusinessInformationResponse | null>(null);
  public readonly businessInformation = this.businessInfo.asReadonly();
  private bankList = signal<any[]>([]);
  public readonly bankListData = this.bankList.asReadonly();
  private businessOwners = signal<any[]>([]);
  public readonly businessOwnersData = this.businessOwners.asReadonly();
  private businessDocuments = signal<DocumentResponseDTO | null>(null);
  public readonly businessDocumentsData = this.businessDocuments.asReadonly();
  private dialog = inject(Dialog);
  private location = inject(Location);

  onProceedToDashboard() {
    this.dialog
      .openConfirmDialog(
        'Proceed to Dashboard',
        'Proceeding to dashboard means your information inputted on this page would not be saved',
      )
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.router.navigate(['/main/dashboard']);
        }
      });
  }

  onBack() {
    this.location.back();
  }

  getBankList() {
    this.gettingBankList.set(true);
    this.service
      .getBankList()
      .pipe(finalize(() => this.gettingBankList.set(false)))
      .subscribe({
        next: (response: FinancialInstitution[]) => {
          this.bankList.set(
            response.map((bank) => ({
              label: bank.institutionName,
              value: bank.institutionCode,
            })),
          );
        },
        error: () => this.bankList.set([]),
      });
  }

  getBusinessInformation() {
    this.gettingBusinessInfo.set(true);
    this.service
      .getBusinessInformation(this.authService.getUserId() || '')
      .pipe(finalize(() => this.gettingBusinessInfo.set(false)))
      .subscribe({
        next: (response) => {
          this.businessInfo.set(response.data);
        },
        error: () => this.businessInfo.set(null),
      });
  }

  getOwners() {
    this.gettingOwners.set(true);
    this.service
      .getOwners()
      .pipe(finalize(() => this.gettingOwners.set(false)))
      .subscribe({
        next: (response) => {
          this.businessOwners.set(response.data?.owners ?? []);
        },
        error: () => this.businessOwners.set([]),
      });
  }

  getDocuments() {
    this.gettingDocuments.set(true);
    this.service
      .getDocuments()
      .pipe(finalize(() => this.gettingDocuments.set(false)))
      .subscribe({
        next: (response) => {
          this.businessDocuments.set(response.data);
        },
        error: () => this.businessDocuments.set(null),
      });
  }

  submitBusinessInformation(payload: any) {
    this.isLoading.set(true);
    return this.service
      .submitBusinessInfo(payload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          if (response && response.success) {
            this.router.navigate(['/kyc/business-verification']);
            this.notification.success(
              'Success',
              response.message || 'Business information submitted successfully',
            );
            return;
          }
          this.notification.error(
            'Error',
            response?.message ||
              'An error occurred while submitting business information',
          );
        },
        error: (error: any) => {
          this.notification.error(
            'Error',
            error?.message ||
              'An error occurred while submitting business information',
          );
        },
      });
  }

  submitBusinessVerification(payload: any, isRegistered: boolean) {
    this.isLoading.set(true);
    return this.service
      .submitBusinessVerification(payload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          if (response && response.success) {
            if (isRegistered) {
              this.router.navigate(['/kyc/document-upload']);
            } else {
              this.router.navigate(['/kyc/bank-information']);
            }
            this.notification.success(
              'Success',
              response.message ||
                'Business verification submitted successfully',
            );
            return;
          }
          this.notification.error(
            'Error',
            response?.message ||
              'An error occurred while submitting business verification',
          );
        },
        error: (error: any) => {
          this.notification.error(
            'Error',
            error?.message ||
              'An error occurred while submitting business verification',
          );
        },
      });
  }

  submitDocuments(payload: any) {
    this.isLoading.set(true);
    return this.service
      .uploadDocuments(payload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          if (response && response.success) {
            this.router.navigate(['/kyc/bank-information']);
            this.notification.success(
              'Success',
              response.message || 'Documents uploaded successfully',
            );
            return;
          }
          this.notification.error(
            'Error',
            response?.message || 'An error occurred while uploading documents',
          );
        },
        error: (error: any) => {
          this.notification.error(
            'Error',
            error?.message || 'An error occurred while uploading documents',
          );
        },
      });
  }

  submitBankInfo(payload: any) {
    this.isLoading.set(true);
    return this.service
      .submitBankInformation(payload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: any) => {
          if (response && response.success) {
            this.router.navigate(['/kyc/business-details']);
            this.notification.success(
              'Success',
              response.message || 'Bank information submitted successfully',
            );
            return;
          }
          this.notification.error(
            'Error',
            response?.message ||
              'An error occurred while submitting bank information',
          );
        },
        error: (error: any) => {
          this.notification.error(
            'Error',
            error?.message ||
              'An error occurred while submitting business verification',
          );
        },
      });
  }

  submitOnlineInfo(payload: any) {
    this.isLoading.set(true);
    return this.service
      .submitOnlineInfo(payload)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response && response.success) {
            this.router.navigate(['/kyc/review']);
            this.notification.success(
              'Success',
              response.message || 'Online information submitted successfully',
            );
            return;
          }
          this.notification.error(
            'Error',
            response?.message ||
              'An error occurred while submitting online information',
          );
        },
        error: (error: any) => {
          this.notification.error(
            'Error',
            error?.message ||
              'An error occurred while submitting online information',
          );
        },
      });
  }

  submitReview() {
    this.isLoading.set(true);
    return this.service
      .review()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response && response.success) {
            this.router.navigate(['/main/dashboard']);
            this.dialog.openSuccessDialog(
              'Verification in Progress',
              'We have successfully received your business information,  You can expect to receive feedback within 24 to 48 hours.',
              'Proceed to Dashboard',
            );
            this.notification.success(
              'Success',
              response.message || 'KYC submitted for review successfully',
            );
            return;
          }
          this.notification.error(
            'Error',
            response?.message || 'An error occurred while submitting KYC',
          );
        },
        error: (error: any) => {
          this.notification.error(
            'Error',
            error?.message || 'An error occurred while submitting KYC',
          );
        },
      });
  }
}
